// Spanish UI strings, namespaced by component/page. Same shape as
// en/strings.js — read in components via `const t = useTranslation();` then
// `t.<namespace>.<key>` (or `t.<namespace>.<key>(arg)` for the interpolated
// entries that are functions).
//
// NOTE: this file currently ships as a verbatim copy of the English bundle and
// still reads in English — the translations are filled in here separately. Keep
// the keys/shape identical to en/strings.js when translating.
export default {
  common: {
    appName: "Gol Predictor",
    cancel: "Cancelar",
    save: "Guardar",
    saving: "Guardando…",
    close: "Cerrar",
    copy: "Copiar",
    share: "Compartir",
    join: "Unirse",
    // Score/result not yet available.
    tbd: "Pend.",
    // `zone` is the local time-zone abbreviation, e.g. "PDT".
    localTimeNote: zone =>
      `Todos los horarios se muestran en tu zona horaria local (${zone}).`,
  },

  header: {
    activeGroup: "Grupo activo:",
    joinGroup: "+ Unirse a un grupo",
    leaveGroup: "Salir de este grupo",
    signOut: "Cerrar sesión",
    newMessages: "Nuevos mensajes",
    newMessagesCount: n => `${n} Nuevos mensajes`,
    sendNewMessage: "Enviar nuevo mensaje",
    notificationReminder: "No olvides guardar",
  },

  sidebar: {
    leagues: "Ligas",
    calendar: "Calendario",
    leaderboards: "Tabla de Posiciones",
    admin: "Administración",
  },

  login: {
    login: "Iniciar sesión",
    username: "Nombre de usuario",
    password: "Contraseña",
    loginError: "Algo está mal con tu inicio de sesión o contraseña :(",
    badPassword: "Mala contraseña",
  },

  joinGroup: {
    title: "Unirse a un grupo",
    groupName: "Nombre del grupo",
    passcode: "Código de acceso",
    joinError: "No se pudo unir al grupo",
  },

  layout: {
    groupsError: "Algo salió mal al cargar tus grupos. Por favor, inténtalo de nuevo más tarde.",
    noGroupsTitle: "No formas parte de ningún grupo.",
    noGroupsBody: "Pide a un administrador que te agregue a un grupo, o únete a uno con un código de acceso.",
    joinGroup: "Unirse a un grupo",
    leaveGroupTitle: "Salir del grupo",
    leaveGroupConfirm: group => `¿Seguro que quieres salir de "${group}"?`,
    groupLeft: "Saliste del grupo",
    couldNotLeaveGroup: "No se pudo salir del grupo",
  },

  fixtures: {
    date: "Fecha",
    teams: "Equipos",
    status: "Estatus",
    prediction: "Predicción",
    actualScore: "Resultado",
    points: "Puntos",
    noFixtures: "No se encontraron partidos",
    savedSnackbar: "Tus predicciones han sido guardadas",
    couldNotSave: "No se pudieron guardar las predicciones",
    couldNotLoad: "No se pudieron cargar los partidos",
    couldNotGetPredictions: "No se pudieron obtener las predicciones",
    selectAll: "Seleccionar todo",
    clearSelection: "Limpiar",
    hideScheduled: "Ocultar programados",
    copyToGroup: "Copiar a grupo…",
    copyTitle: "Copiar predicciones a otro grupo",
    copyTargetLabel: "Copiar a los grupos",
    // `existing` is the target group's current prediction, e.g. "2 - 1".
    copyConflict: existing => `Ya predicho: ${existing}`,
    // `count` is how many selected matches had no prediction to copy.
    copySkipped: count =>
      `${count} ${count === 1 ? "partido seleccionado no tiene" : "partidos seleccionados no tienen"} predicción y ${count === 1 ? "fue omitido" : "fueron omitidos"}.`,
    copying: "Copiando…",
    copied: "Predicciones copiadas",
    couldNotCopy: "No se pudieron copiar las predicciones",
    noEligibleGroups:
      "No estás en ningún otro grupo que tenga esta liga.",
    nothingToCopy:
      "Ninguno de los partidos seleccionados tiene una predicción para copiar.",
  },

  calendar: {
    title: "Calendario",
    showAllPredictions: "Mostrar todas las predicciones",
    liveNow: "En vivo",
    matches: "Partidos",
    noMatches: "No hay partidos en este día.",
    today: "Hoy",
    viewPredictions: "Ver predicciones",
    editPredictions: "Cambiar predicciones",
    scheduled: "Programado",
    missingPredictionsTitle: "Predicciones faltantes",
    couldNotLoad: "No se pudo cargar el calendario",
    couldNotSave: "No se pudieron guardar las predicciones",
    copied: "Predicciones copiadas al portapapeles",
    saved: "Predicciones guardadas",
  },

  // Labels in the plain-text share/clipboard output (pasted into chat) and the
  // live/finished score readouts. Surrounding punctuation/layout stays in code;
  // only these words are translated, e.g. "(LIVE - 45')", "(Reg: 1 - 1)",
  // "(5pts: 5CR)".
  share: {
    liveLabel: "LIVE",
    regLabel: "Reg",
    ptsSuffix: "pts",
  },

  // The shared per-match predictions table (calendar dialog + inline expansion).
  predictions: {
    noPredictions: "No hay predicciones para este partido.",
    player: "Jugador",
    prediction: "Predicción",
    points: "Puntos",
    pointsInfo: "Información de puntos",
  },

  leaderboards: {
    title: "Tabla de Posiciones",
    aggregate: "Agregado",
    // Uppercase header used in the shared plain-text copy.
    aggregateHeader: "AGREGADO",
    rank: "Posición",
    player: "Jugador",
    settled: "Final",
    live: "En vivo",
    total: "Total",
    points: "Puntos",
    noStandings: "No hay resultados aún",
    copied: "Tabla de posiciones copiada al portapapeles",
    couldNotLoad: "No se pudieron cargar las tablas de posiciones",
  },

  admin: {
    title: "Administración",
    exportTitle: "Exportar predicciones anteriores",
    exportDescription:
      "Descargar un CSV con las predicciones de todos los jugadores para los partidos pasados en todas las ligas de este grupo.",
    exporting: "Exportando…",
    couldNotExport: "No se pudieron exportar las predicciones",
    exportMissingTitle: "Exportar predicciones faltantes",
    exportMissingDescription:
      "Descargar un CSV que lista, por partido, los jugadores del grupo que no tienen predicción.",
    exportMissingDialogTitle: "Exportar predicciones faltantes",
    exportMissingPast: "Partidos pasados / bloqueados",
    exportMissingFuture: "Partidos futuros",
    couldNotExportMissing: "No se pudieron exportar las predicciones faltantes",
    scoringTitle: "Modo de puntuación",
    scoringDescription:
      "Elige cómo se puntúan las predicciones de este grupo. Cambiarlo recalcula los puntos de todos los partidos.",
    scoringLabel: "Modo de puntuación",
    saving: "Guardando…",
    scoringSaved: "Modo de puntuación actualizado",
    couldNotSaveScoring: "No se pudo actualizar el modo de puntuación",
    usersTitle: "Administrar usuarios",
    usersDescription:
      "Agrega jugadores a este grupo y define sus contraseñas de acceso. Las contraseñas se ingresan en texto plano y se guardan cifradas de forma segura.",
    addUserButton: "Agregar usuario",
    addUserTitle: "Agregar usuario",
    addUserLabel: "Usuario",
    userAdded: "Usuario agregado",
    couldNotAddUser: "No se pudo agregar el usuario",
    changePasswordButton: "Cambiar contraseña",
    changePasswordTitle: member => `Definir contraseña para ${member}`,
    passwordLabel: "Nueva contraseña",
    passwordSet: "Contraseña actualizada",
    couldNotSetPassword: "No se pudo guardar la contraseña",
    removeMemberButton: "Eliminar",
    removeMemberConfirm: member => `¿Eliminar a ${member} de este grupo?`,
    memberRemoved: "Usuario eliminado",
    couldNotRemoveMember: "No se pudo eliminar al usuario",
    noMembers: "Aún no hay usuarios",
    loadMembersError: "No se pudieron cargar los usuarios",
    // CSV column headers, in the order written to the file.
    csvColumns: [
      "liga",
      "fecha del partido",
      "equipo local",
      "equipo visitante",
      "resultado local",
      "resultado visitante",
      "nombre del jugador",
      "predicción local",
      "predicción visitante",
      "puntos",
      "información de puntos",
    ],
    // CSV column headers for the missing-predictions export.
    csvMissingColumns: [
      "liga",
      "fecha del partido",
      "equipo local",
      "equipo visitante",
      "resultado local",
      "resultado visitante",
      "predicciones faltantes",
    ],
  },
};
